const KNOWN_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'protonmail.com',
  'mail.com',
  'yandex.com',
  'live.com',
  'msn.com',
  'comcast.net',
  'att.net',
  'verizon.net',
];

const KNOWN_SECOND_LEVEL_DOMAINS = KNOWN_DOMAINS.map(d => d.split('.')[0]);

const KNOWN_TOP_LEVEL_DOMAINS = [
  'com',
  'net',
  'org',
  'edu',
  'gov',
  'co.uk',
  'ca',
  'de',
  'fr',
  'ru',
  'jp',
  'info',
  'io',
];

// Observed in production Datadog logs (May 2026, 4-hour sample).
// Checked before fuzzy matching for guaranteed correctness.
const STATIC_TYPO_MAP: Record<string, string> = {
  'gamil.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmsil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cl': 'gmail.com',
  'gmail.ccm': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.comf': 'gmail.com',
  'gmail.comj': 'gmail.com',
  'icloud.con': 'icloud.com',
  'ivloud.com': 'icloud.com',
  'iclud.com': 'icloud.com',
  'myyahoo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'alyahoo.com': 'yahoo.com',
  'hitmail.com': 'hotmail.com',
  'hmail.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'protonamil.com': 'protonmail.com',
};

export interface EmailSuggestion {
  original: string;
  suggested: string;
}

export function isStaticTypoDomain(email: string): boolean {
  const atIndex = email.indexOf('@');
  if (atIndex === -1) return false;
  const domain = email
    .slice(atIndex + 1)
    .toLowerCase()
    .trim();
  return domain in STATIC_TYPO_MAP;
}

function sift3Distance(s1: string, s2: string): number {
  if (!s1?.length) return s2?.length || 0;
  if (!s2?.length) return s1.length;

  let c = 0;
  let offset1 = 0;
  let offset2 = 0;
  let lcs = 0;
  const maxOffset = 5;

  while (c + offset1 < s1.length && c + offset2 < s2.length) {
    if (s1.charAt(c + offset1) === s2.charAt(c + offset2)) {
      lcs += 1;
    } else {
      offset1 = 0;
      offset2 = 0;
      for (let i = 0; i < maxOffset; i += 1) {
        if (c + i < s1.length && s1.charAt(c + i) === s2.charAt(c)) {
          offset1 = i;
          break;
        }
        if (c + i < s2.length && s1.charAt(c) === s2.charAt(c + i)) {
          offset2 = i;
          break;
        }
      }
    }
    c += 1;
  }
  return (s1.length + s2.length) / 2 - lcs;
}

function findClosestDomain(
  domain: string,
  domains: string[],
  threshold: number,
): string | null {
  let minDistance = Infinity;
  let closest: string | null = null;

  for (const d of domains) {
    if (domain === d) return domain;
    const dist = sift3Distance(domain, d);
    if (dist < minDistance) {
      minDistance = dist;
      closest = d;
    }
  }

  if (minDistance <= threshold && closest) {
    return closest;
  }
  return null;
}

function parseEmailDomain(email: string): {
  address: string;
  domain: string;
  secondLevelDomain: string;
  topLevelDomain: string;
} | null {
  const parts = email.split('@');
  if (parts.length < 2 || !parts[0] || !parts[1]) return null;

  const domain = parts[1].toLowerCase();
  const domainParts = domain.split('.');
  if (domainParts.length < 2) return null;

  const tld = domainParts.slice(1).join('.');
  const sld = domainParts[0];

  return {
    address: parts[0],
    domain,
    secondLevelDomain: sld,
    topLevelDomain: tld,
  };
}

const DOMAIN_THRESHOLD = 2;
const SLD_THRESHOLD = 2;
const TLD_THRESHOLD = 2;

export function suggestEmailDomain(email: string): EmailSuggestion | null {
  if (!email || !email.includes('@')) return null;

  const parsed = parseEmailDomain(email.trim());
  if (!parsed) return null;

  const { address, domain, secondLevelDomain, topLevelDomain } = parsed;

  // Layer 1: static typo map (guaranteed hits from production data)
  const staticCorrection = STATIC_TYPO_MAP[domain];
  if (staticCorrection) {
    return {
      original: email,
      suggested: `${address}@${staticCorrection}`,
    };
  }

  // Exact match — no suggestion needed
  if (
    KNOWN_SECOND_LEVEL_DOMAINS.includes(secondLevelDomain) &&
    KNOWN_TOP_LEVEL_DOMAINS.includes(topLevelDomain)
  ) {
    return null;
  }

  // Layer 2: fuzzy match against full domain list
  const closestDomain = findClosestDomain(
    domain,
    KNOWN_DOMAINS,
    DOMAIN_THRESHOLD,
  );

  if (closestDomain && closestDomain !== domain) {
    return {
      original: email,
      suggested: `${address}@${closestDomain}`,
    };
  }

  // Layer 3: check SLD and TLD separately
  let correctedDomain = domain;
  let hasCorrection = false;

  const closestSLD = findClosestDomain(
    secondLevelDomain,
    KNOWN_SECOND_LEVEL_DOMAINS,
    SLD_THRESHOLD,
  );
  if (closestSLD && closestSLD !== secondLevelDomain) {
    correctedDomain = correctedDomain.replace(secondLevelDomain, closestSLD);
    hasCorrection = true;
  }

  const closestTLD = findClosestDomain(
    topLevelDomain,
    KNOWN_TOP_LEVEL_DOMAINS,
    TLD_THRESHOLD,
  );
  if (closestTLD && closestTLD !== topLevelDomain && secondLevelDomain !== '') {
    const tldIndex = correctedDomain.lastIndexOf(topLevelDomain);
    if (tldIndex !== -1) {
      correctedDomain = correctedDomain.slice(0, tldIndex) + closestTLD;
      hasCorrection = true;
    }
  }

  if (hasCorrection) {
    return {
      original: email,
      suggested: `${address}@${correctedDomain}`,
    };
  }

  return null;
}
